import { StandardEvents } from '@shopify/events';

/**
 * sis-sticky-cart: DS StickyCartBar (mobile fixed bottom ADD TO CART bar).
 *
 * - The block renders an inline sentinel where the merchant placed it (right
 *   after the buy buttons). The bar shows only once that sentinel scrolls
 *   above the viewport and hides again when it comes back. Visibility flips
 *   instantly: no entrance animation (owner motion rule, hover/press only).
 * - Add to cart submits the section's main product form with
 *   form.requestSubmit() so the stock product-form-component's on:submit
 *   handler runs the add (same guards, quantity, variant queueing, cart
 *   events). The form is located by section scope + data-product-id, the
 *   same context pattern as sis-option-tiles; the form is never cloned.
 * - Availability mirrors the SERVER state: the initial disabled/sold-out
 *   rendering comes from Liquid, and each variant selection re-reads the
 *   productSelect event promise's fetched section HTML (the stock
 *   product-price pattern, CLAUDE.md convention 11b). The live button's
 *   transient disabled flicker during an in-flight add is deliberately
 *   NOT mirrored.
 */
class SisStickyCart extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {Element | null} */
  #section = null;

  /** @type {HTMLElement | null} */
  #bar = null;

  /** @type {HTMLButtonElement | null} */
  #button = null;

  #passed = false;
  #forced = false;

  connectedCallback() {
    this.#bar = this.querySelector('[data-sis-bar]');
    this.#button = this.querySelector('[data-sis-button]');
    const sentinel = this.querySelector('[data-sis-sentinel]');
    if (!this.#bar || !this.#button || !sentinel) return;

    this.#button.addEventListener('click', this.#submit);

    // Observe a REGION (everything above the viewport's top edge), not the
    // 1px sentinel against the plain viewport: a fast flick or programmatic
    // jump can move the sentinel from below-viewport to above-viewport in one
    // frame - intersection stays false -> false, no callback, bar never shows.
    // With rootMargin extending the root far above and pulling its bottom to
    // the viewport top, "sentinel scrolled past" IS the intersecting state,
    // so any arrival above the top edge fires exactly one callback.
    this.#observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;
        this.#passed = entry.isIntersecting;
        this.#render();
      },
      { rootMargin: '100000px 0px -100% 0px' },
    );
    this.#observer.observe(sentinel);

    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);

    // Theme editor: reveal the bar while the block is selected so the owner
    // can style it without scrolling
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:block:select', this.#onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.#onBlockDeselect);
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#onBlockDeselect);
  }

  #render() {
    if (!this.#bar) return;
    this.#bar.hidden = !(this.#passed || this.#forced);
  }

  /** @returns {Element | null} */
  #formComponent() {
    if (!this.#section) return null;
    return this.#section.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`);
  }

  #submit = () => {
    const form = this.#formComponent()?.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  };

  /** @param {any} event */
  #onProductSelect = (event) => {
    event.promise
      .then((/** @type {any} */ { detail }) => {
        const main = detail?.html?.querySelector('product-form-component [ref="addToCartButton"]');
        if (main) this.#setAvailable(!main.hasAttribute('disabled'));
      })
      .catch((/** @type {any} */ error) => {
        if (error?.name !== 'AbortError') console.warn('[sis-sticky-cart]', error);
      });
  };

  /** @param {boolean} available */
  #setAvailable(available) {
    if (!this.#button) return;
    this.#button.disabled = !available;
    const label = this.#button.querySelector('[data-sis-label-available]');
    const soldout = this.#button.querySelector('[data-sis-label-soldout]');
    if (label) label.hidden = !available;
    if (soldout) soldout.hidden = available;
  }

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    if (event.target !== this) return;
    this.#forced = true;
    this.#render();
  };

  /** @param {Event} event */
  #onBlockDeselect = (event) => {
    if (event.target !== this) return;
    this.#forced = false;
    this.#render();
  };
}

if (!customElements.get('sis-sticky-cart')) {
  customElements.define('sis-sticky-cart', SisStickyCart);
}
